import React, { useCallback, useRef, useState } from 'react';

import type { Command, EditorState } from 'prosemirror-state';
import type { IntlShape } from 'react-intl';

import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type {
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import type { Node as PMNode, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { InlineSuggestionToolbar } from '@atlassian/editor-plugin-ai/InlineSuggestionToolbar';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import { REMIX_DENY_LIST_V1 } from '@atlassian/malleable-ui-components/remix/remix-config';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { malleableUiPluginKey } from '../../pm-plugins/plugin-key';
import { remixThinkingPluginKey } from '../../pm-plugins/remix-thinking/remix-thinking-plugin';
import { messages } from '../messages';
import {
	ASK_ROVO_MEDIA_NODE_TYPES,
	isConsideredEmptyForRemix,
	isRemixInlineDropdownVariant,
	REMIX_FLOATING_TOOLBAR_TEST_ID,
	shouldShowRemixForSelectionContext,
} from '../remix-config';

import {
	buildTypeSwitcherDropdownItems,
	getAskRovoTargetId,
} from './floating-toolbar/floating-toolbar-remix-variant';

export function getUnallowedNodes(schema: Schema): Set<NodeType> {
	return new Set(REMIX_DENY_LIST_V1.map((name) => schema.nodes[name]).filter(Boolean));
}

export function getMediaNodesForAskRovo(schema: Schema): Set<NodeType> {
	return new Set(ASK_ROVO_MEDIA_NODE_TYPES.map((name) => schema.nodes[name]).filter(Boolean));
}

const getMediaNodeForAskRovo = (node: PMNode): PMNode | undefined => {
	if (node.type.name === 'media') {
		return node;
	}
	if (node.type.name === 'mediaSingle') {
		return node.firstChild ?? undefined;
	}
	return undefined;
};

const isImageMediaNodeForAskRovo = (node: PMNode): boolean => {
	const mediaNode = getMediaNodeForAskRovo(node);
	if (!mediaNode || mediaNode.type.name !== 'media') {
		return false;
	}

	if (mediaNode.attrs?.type === 'external') {
		return true;
	}

	const fileMimeType = mediaNode.attrs?.__fileMimeType;
	return typeof fileMimeType === 'string' && fileMimeType.includes('image/');
};

const remixFloatingToolbarClickAnalyticsPayload = {
	action: ACTION.CLICKED,
	actionSubject: ACTION_SUBJECT.BUTTON,
	actionSubjectId: 'remixModalFromToolbar',
	eventType: EVENT_TYPE.UI,
	source: 'remix',
} as const;

interface RemixFloatingToolbarButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	intl: IntlShape;
	isDisabled: boolean;
	node: PMNode;
	view: EditorView | undefined;
}

/**
 * Inline-dropdown variant of the floating-toolbar Remix item, rendered as a persistent React
 * component so the anchored picker can hang off a stable trigger. `openRemixModal` is only
 * dispatched once an option is picked — dispatching it just to show the picker moves the
 * ProseMirror selection, which makes the surrounding floating toolbar re-evaluate and detach
 * the popup from its trigger.
 */
function RemixFloatingToolbarButton({
	api,
	intl,
	isDisabled,
	node,
	view,
}: RemixFloatingToolbarButtonProps): React.JSX.Element {
	const { formatMessage } = intl;
	const isCreateKeywordEnabled = expValEquals('cc_maui_create_keyword', 'isEnabled', true);

	const { isRemixing } = useSharedPluginStateWithSelector(api, ['malleableUi'], (states) => ({
		isRemixing:
			states.malleableUiState?.isRemixModalOpen ||
			false ||
			states.malleableUiState?.isThinking ||
			false,
	}));

	const effectiveDisabled = isDisabled || isRemixing;

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	// Captured at click time so the remix targets the node the user clicked on rather than
	// whatever the selection happens to be once they finish picking an option.
	const nodePosRef = useRef<number | undefined>(undefined);

	const handleClick = useCallback(() => {
		if (effectiveDisabled || !view) {
			return;
		}
		const $pos = view.state.selection.$from;
		let nodePos: number | undefined;
		for (let depth = $pos.depth; depth > 0; depth--) {
			if ($pos.node(depth) === node) {
				nodePos = $pos.before(depth);
				break;
			}
		}
		nodePosRef.current = nodePos;
		api.analytics?.actions.fireAnalyticsEvent<
			typeof remixFloatingToolbarClickAnalyticsPayload,
			'customEventType'
		>(remixFloatingToolbarClickAnalyticsPayload);
		setIsDropdownOpen((open) => !open);
	}, [api, effectiveDisabled, node, view]);

	const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

	const handleOptionSelect = useCallback(
		({ type, subType }: RemixOptionSelectionParameters) => {
			closeDropdown();
			api.core.actions.execute(
				api.malleableUi.commands.openRemixModal({
					nodePos: nodePosRef.current,
					nodeType: node.type.name,
					metadata: {
						interactionSource: 'remixModalFromFloatingToolbar',
						preselectedType: type,
						preselectedSubType: subType,
					},
					source: 'floatingToolbar',
				}),
			);
		},
		[api, closeDropdown, node],
	);

	const tooltipContent = isRemixing
		? formatMessage(
				isCreateKeywordEnabled ? messages.createInProgressTooltip : messages.remixInProgressTooltip,
			)
		: formatMessage(
				isCreateKeywordEnabled ? messages.createToolbarTooltip : messages.remixToolbarTooltip,
			);

	const renderTrigger = (triggerProps: TriggerProps) => (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				{...triggerProps}
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={REMIX_FLOATING_TOOLBAR_TEST_ID}
				onClick={handleClick}
				isSelected={isDropdownOpen}
				isDisabled={effectiveDisabled}
			>
				{formatMessage(
					isCreateKeywordEnabled ? messages.createToolbarLabel : messages.remixToolbarLabel,
				)}
			</ToolbarButton>
		</ToolbarTooltip>
	);

	return (
		<RemixContainer
			presentation="popup"
			trigger={renderTrigger}
			popupPlacement="bottom-start"
			popupOffset={[0, 4]}
			isPopupOpen={isDropdownOpen}
			isThinking={false}
			onClose={closeDropdown}
			onOptionSelect={handleOptionSelect}
		/>
	);
}

export function getOverriddenFloatingToolbarConfig(
	api?: ExtractInjectionAPI<MalleableUiPlugin>,
	options?: { shouldHideToolbarWhileRemixing?: boolean },
): (
	state: EditorState,
	intl: IntlShape,
	_providerFactory: unknown,
	activeConfigs?: Array<FloatingToolbarConfig>,
) => FloatingToolbarConfig | undefined {
	if (!api?.toolbar) {
		return () => undefined;
	}

	return (
		state: EditorState,
		intl: IntlShape,
		_providerFactory: unknown,
		activeConfigs?: Array<FloatingToolbarConfig>,
	): FloatingToolbarConfig | undefined => {
		if (!activeConfigs?.length) {
			return;
		}

		const unallowedNodes = getUnallowedNodes(state.schema);
		const malleableUiState = malleableUiPluginKey.getState(state);
		const remixThinkingState = remixThinkingPluginKey.getState(state);
		const isRemixing =
			malleableUiState?.isRemixModalOpen || false || remixThinkingState?.isThinking || false;
		const areAiEntryPointsDisabled =
			api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled ?? false;
		activeConfigs.forEach((config) => {
			const configNodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isTableConfig = !Array.isArray(config.nodeType) && config.nodeType.name === 'table';

			// When an element is being remixed (modal open or thinking bar showing), hide its
			// element/floating toolbar entirely by emptying its items so the floating toolbar plugin
			// won't show it.
			//
			// Behind `cc-maui-phase-3` this applies to every element type. Previously the toolbar
			// was only hidden for tables, which left the element toolbar visible for other nodes
			// (e.g. panels and column layouts) while the thinking bar was showing. We skip the
			// inline-suggestion toolbar so the in-editor remix loading bar itself is not affected.
			// When the gate is off we retain the legacy table-only behaviour below.
			//
			// `shouldHideToolbarWhileRemixing` is an explicit opt-in from the product (only
			// Confluence sets this to true). This replaces the previous `config.className !==
			// undefined` heuristic, which accidentally affected products (e.g. Jira) that don't
			// set a className on their element toolbar configs.
			if (
				isRemixing &&
				options?.shouldHideToolbarWhileRemixing &&
				config.className !== InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS &&
				fg('cc-maui-phase-3')
			) {
				config.items = [];
				return;
			}

			// Legacy behaviour (gate off): hide only the table element toolbar while remixing.
			if (isTableConfig && isRemixing && !fg('cc-maui-phase-3')) {
				config.items = [];
				return;
			}

			// Check if every config node type is denied or inline. If the config also
			// includes multiBodiedExtension we defer the final decision to the items
			// callback where we can inspect the actual node's extensionKey (so that
			// allowed MBE variants like native tabs still get the Remix button).
			const hasMBENodeType = configNodeTypes.some((nt) => nt.name === 'multiBodiedExtension');
			const allDeniedOrInline = configNodeTypes.every(
				(nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline,
			);

			if (
				(config.className !== undefined &&
					config.className === InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS) ||
				(isTableConfig && config.items.length === 0) ||
				(configNodeTypes.some((nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline) &&
					!hasMBENodeType)
			) {
				return;
			}

			// When all node types are denied/inline and the only reason we didn't
			// skip is because the config includes MBE, we need to gate the Remix
			// button on the actual node being an allowed MBE variant.
			const requiresMBECheck = allDeniedOrInline && hasMBENodeType;

			const toolbarItems = config.items;
			if (Array.isArray(toolbarItems)) {
				// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
				expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);
				const remixButton: FloatingToolbarItem<Command> = {
					id: 'editor.malleable-ui.remix',
					type: 'button',
					title: intl.formatMessage(
						expValEquals('cc_maui_create_keyword', 'isEnabled', true)
							? messages.createToolbarLabel
							: messages.remixToolbarLabel,
					),
					tooltipContent: isRemixing
						? intl.formatMessage(
								expValEquals('cc_maui_create_keyword', 'isEnabled', true)
									? messages.createInProgressTooltip
									: messages.remixInProgressTooltip,
							)
						: intl.formatMessage(
								expValEquals('cc_maui_create_keyword', 'isEnabled', true)
									? messages.createToolbarTooltip
									: messages.remixToolbarTooltip,
							),
					showTitle: true,
					hideTooltipOnClick: false,
					icon: AiGenerativeRemixIcon,
					onClick: () => false,
					disabled: isRemixing || areAiEntryPointsDisabled,
					testId: REMIX_FLOATING_TOOLBAR_TEST_ID,
				};
				const remixToolbarItems: FloatingToolbarItem<Command>[] = [
					remixButton,
					{
						type: 'separator',
						fullHeight: true,
					},
				];

				config.items = (node) => {
					// When the config was only kept because it contains MBE, check
					// whether the actual selected node is an allowed MBE variant
					// (e.g. native tabs). If not, return the original items without
					// the Remix button.
					if (requiresMBECheck && !shouldShowRemixForSelectionContext({ node })) {
						return toolbarItems;
					}

					const isRemixButtonDisabled =
						isRemixing || areAiEntryPointsDisabled || isConsideredEmptyForRemix(node);

					if (isRemixInlineDropdownVariant()) {
						return [
							{
								type: 'custom',
								fallback: [],
								render: (view) => (
									<RemixFloatingToolbarButton
										api={api}
										intl={intl}
										isDisabled={isRemixButtonDisabled}
										node={node}
										view={view}
									/>
								),
							},
							{
								type: 'separator',
								fullHeight: true,
							},
							...toolbarItems,
						];
					}

					remixButton.onClick = (editorState) => {
						if (isRemixing) {
							return false;
						}
						const $pos = editorState.selection.$from;
						let nodePos: number | undefined;
						for (let depth = $pos.depth; depth > 0; depth--) {
							if ($pos.node(depth) === node) {
								nodePos = $pos.before(depth);
								break;
							}
						}
						api.analytics?.actions.fireAnalyticsEvent<
							typeof remixFloatingToolbarClickAnalyticsPayload,
							'customEventType'
						>(remixFloatingToolbarClickAnalyticsPayload);
						api.core.actions.execute(
							api.malleableUi.commands.openRemixModal({
								nodePos,
								nodeType: node.type.name,
								metadata: {
									interactionSource: 'remixModalFromFloatingToolbar',
								},
								source: 'floatingToolbar',
							}),
						);
						return true;
					};
					remixButton.disabled = isRemixButtonDisabled;
					return [...remixToolbarItems, ...toolbarItems];
				};
			}
		});

		const mediaNodesForAskRovo = getMediaNodesForAskRovo(state.schema);
		const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
		const showAskRovoForMedia =
			fg('cc-maui-phase-2') ||
			(isViewMode && expValEquals('cc-maui-experiment', 'isEnabled', true));
		if (!mediaNodesForAskRovo.size || !showAskRovoForMedia) {
			return;
		}

		const askRovoButton = api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
			intl,
			disabled:
				api?.malleableUi?.actions?.shouldDisableRemix?.() && fg('platform_editor_ai_remix_toolbar'),
			target: isViewMode ? 'default' : 'image',
		});
		if (!askRovoButton) {
			return;
		}

		const shouldShowAskRovoForMediaNode = (node: PMNode) =>
			mediaNodesForAskRovo.has(node.type) &&
			!node.type.isInline &&
			isImageMediaNodeForAskRovo(node);

		const buildTypeSwitcherItemsForMediaNode = (
			node: PMNode,
		): FloatingToolbarItem<Command> | undefined => {
			return buildTypeSwitcherDropdownItems({
				node,
				schema: state.schema,
				state,
				intl,
				disabled: Boolean(isRemixing || areAiEntryPointsDisabled),
				api,
			});
		};

		// Build a per-node wrapped Ask Rovo button whose onClick stages the
		// selected media's target id (localId, falling back to file id) as the
		// inline-Rovo streaming target.
		const buildAskRovoItemsForMediaNode = (node: PMNode): FloatingToolbarItem<Command>[] => {
			const wrappedAskRovoButton: FloatingToolbarItem<Command> = {
				...askRovoButton,
				...(isViewMode ? { supportsViewMode: true } : {}),
				onClick: (innerState, dispatch, view) => {
					if (fg('cc-maui-phase-2')) {
						const targetId = getAskRovoTargetId(node);
						if (targetId) {
							api?.aiExperience?.actions?.stageInlineRovoStreaming?.(targetId);
						}
					}
					return askRovoButton.onClick ? askRovoButton.onClick(innerState, dispatch, view) : false;
				},
			};
			return [
				wrappedAskRovoButton,
				{
					type: 'separator',
					fullHeight: true,
					...(isViewMode ? { supportsViewMode: true } : {}),
				},
			];
		};

		activeConfigs.forEach((config) => {
			const nodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isMediaConfig = nodeTypes.some(
				(nodeType) => mediaNodesForAskRovo.has(nodeType) && !nodeType.isInline,
			);
			if (!isMediaConfig) {
				return;
			}

			const items = config.items;

			if (Array.isArray(items)) {
				config.items = (node) => {
					if (!shouldShowAskRovoForMediaNode(node)) {
						return items;
					}
					const withAskRovo = [...buildAskRovoItemsForMediaNode(node), ...items];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			} else if (typeof items === 'function') {
				config.items = (node) => {
					const baseItems = items(node);
					if (!shouldShowAskRovoForMediaNode(node)) {
						return baseItems;
					}
					const withAskRovo = [...buildAskRovoItemsForMediaNode(node), ...baseItems];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			}
		});
	};
}
